import { useEffect, useState } from "react";
import { useRenterDashboardContext } from "../renter-dashboard/useRenterDashboardContext";
import { formatDate } from "@/common/helpers/date.helpers";
import { formatCurrency } from "@/common/helpers/mask.helpers";
import { CalendarIcon, ChevronDownIcon, ClockIcon, DollarSignIcon, Edit3Icon, RotateCcwIcon, Trash2Icon } from "lucide-react";

import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react";
import ButtonWithLoader from "@/common/components/ui/loading/ButtonLoading";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { InfoCard } from "../../helpers/InfoCard";
import { CopyableText } from "../../helpers/CopyableText";
import { ModalEditSubscription } from "./components/modals/ModalEditSubscription";
import { getRenterSubscription, syncSubscriptionAsaas } from "@/common/services/subscription.api";
import { ModalDeleteSubscription } from "./components/modals/ModalDeleteSubscription";
import { ISubscription } from "@/types/subscription.type";
import { ModalCreatedSubscription } from "./components/modals/ModalCreatedSubscription";
import Permission from "@/common/components/auth/Permission";


export default function RenterDashboardSubscription() {
  const { notifySuccess, notifyError } = useToastContext();
  const { renter } = useRenterDashboardContext();
  const [subscriptions, setSubscriptions] = useState<ISubscription[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSync, setIsLoadingSync] = useState(false);
  const [openModalEditSubscription, setOpenModalEditSubscription] = useState(false);
  const [openModalDeleteSubscription, setOpenModalDeleteSubscription] = useState(false);
  const [openModalCreatedSubscription, setOpenModalCreatedSubscription] = useState(false);
  const [selectedSub, setSelectedSub] = useState<ISubscription | null>(null);


  const renterId = renter._id;

  const handleEdit = (sub: ISubscription) => {
    setSelectedSub(sub);
    setOpenModalEditSubscription(true);
  };

  const handleDelete = (sub: ISubscription) => {
    setSelectedSub(sub);
    setOpenModalDeleteSubscription(true);
  };

  const handleCloseModals = async () => {
    setOpenModalEditSubscription(false);
    setOpenModalDeleteSubscription(false);
    setOpenModalCreatedSubscription(false);
    setSelectedSub(null);
    // Recarregar dados após fechar modal
    if (renterId) {
      await loadSubscriptionRenter(renterId);
    }
  };

  const handleSyncAsaasSubscription = async (renterId: string) => {
    try {
      setIsLoadingSync(true);
      const result = await syncSubscriptionAsaas(renterId);
      const { ok } = result;

      if (ok) {
        notifySuccess("Assinatura sincronizada com sucesso:");

        // ✅ Recarrega apenas os dados das assinaturas
        await loadSubscriptionRenter(renterId);
      } else {
        notifyError("Erro ao sincronizar assinatura:");
      }
    } catch (error) {
      console.error("Erro ao sincronizar assinatura:", error);
    } finally {
      setIsLoadingSync(false);
    }
  };


  // Mova a função para fora do useEffect para poder reutilizar
  const loadSubscriptionRenter = async (renterId: string) => {
    if (!renterId) return;

    setIsLoading(true);
    try {
      const result = await getRenterSubscription(renterId);
      const { ok, data } = result;

      if (ok && data) {
        setSubscriptions(data);
      }
    } catch (error) {
      console.error("Erro ao carregar assinaturas:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (renterId) {
      loadSubscriptionRenter(renterId);
    }
  }, [renterId]);

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-red-100 text-red-800';
      case 'expired':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getSubscriptionTypeLabel = (type: string) => {
    switch (type.toLowerCase()) {
      case 'monthly':
        return 'Mensal';
      case 'quarterly':
        return 'Trimestral (3 Meses)';
      case 'yearly':
        return 'Anual (1 Ano)';
      case 'thirtymonths':
        return 'Trinta meses';
      case 'thirtyfourmonths':
        return 'Trinta e quatro meses';
      case 'biennale':
        return '24 meses (2 Anos)';
      case 'triennial':
        return '36 meses (3 Anos)';
      default:
        return type;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!subscriptions.length) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="text-center text-gray-500">
          <h3 className="text-lg font-medium mb-2">Nenhuma assinatura encontrada</h3>
          <p>Este cliente não possui assinaturas ativas no momento.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 w-full max-w-full mx-auto min-h-screen">
      <section id="modals-section">
        {openModalEditSubscription && selectedSub && (
          <ModalEditSubscription
            onClose={handleCloseModals}
            subscription={selectedSub}
            renter={renter}
          />
        )}

        {/* Modal de Exclusão */}
        {openModalDeleteSubscription && selectedSub && (
          <ModalDeleteSubscription
            subscriptionId={selectedSub._id!}
            renterId={renter._id}
            onClose={handleCloseModals}
          />
        )}
        {
          openModalCreatedSubscription && (
            <ModalCreatedSubscription
              onClose={handleCloseModals}
              renter={renter}
            />
          )
        }
      </section>

      <div className="flex items-center mb-6 w-full">
        <div className="flex-none">
          <ButtonWithLoader
            onClick={() => handleSyncAsaasSubscription(renterId!)}
            isLoading={isLoadingSync}
          >
            Sincronizar com Asaas
          </ButtonWithLoader>
        </div>

        <div className="flex-1 text-center px-4">
          <h1 className="text-2xl font-bold text-gray-900">Assinaturas</h1>
          <span className="mt-2 inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
            {subscriptions.length} {subscriptions.length === 1 ? 'assinatura' : 'assinaturas'}
          </span>
        </div>

        <Permission role="finance">

          <div className="flex-none">
            <ButtonWithLoader
              onClick={() => setOpenModalCreatedSubscription(true)}
              isLoading={false}
            >
              Adicionar Assinatura
            </ButtonWithLoader>
          </div>
        </Permission>
      </div>

      {/* Grid com 2 cards por linha em telas pequenas pra cima */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        {subscriptions
          .slice()
          .sort((a, b) => {
            const aIsActive = a.status?.toLowerCase() === "active" ? 0 : 1;
            const bIsActive = b.status?.toLowerCase() === "active" ? 0 : 1;
            return aIsActive - bIsActive;
          })
          .map((sub) => (
            <div
              key={sub._id}
              className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col hover:shadow-md transition-shadow"
            >
              {/* Cabeçalho com status */}
              <div className={`p-4 ${sub.status === 'ACTIVE' ? 'bg-green-50' : sub.status === 'EXPIRED' ? 'bg-yellow-50' : 'bg-gray-50'} border-b`}>
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0 pr-4">
                    <h2 className="text-lg font-semibold text-gray-900 truncate" title={sub.planName}>
                      {sub.planName}
                    </h2>
                    <div className="flex items-center mt-1 space-x-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(sub.status)}`}>
                        {sub.status === 'ACTIVE' ? 'Ativa' : sub.status === 'EXPIRED' ? 'Expirada' : 'Inativa'}
                      </span>
                      <span className="text-xs text-gray-500">
                        {getSubscriptionTypeLabel(sub.subscriptionType)}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    {/* Valor */}
                    <div className="text-right">
                      <div className="text-xl font-bold text-gray-900 whitespace-nowrap">
                        {formatCurrency(sub.value)}
                      </div>
                      <div className="text-xs text-gray-500">mensais</div>
                    </div>

                    {/* Ações */}
                    <Permission role="finance">
                      <div className="flex flex-col gap-2">
                        {/* Botão Editar */}
                        <button
                          type="button"
                          className="inline-flex items-center justify-center p-2 rounded-md text-blue-600 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          aria-label={`Editar assinatura ${sub.planName}`}
                          title="Editar assinatura"
                          onClick={() => handleEdit(sub)}
                          disabled={!(sub.status === 'ACTIVE' || sub.status === 'EXPIRED')}
                        >
                          <Edit3Icon className="h-5 w-5" />
                        </button>

                        {/* Botão Excluir */}
                        <button
                          type="button"
                          className="inline-flex items-center justify-center p-2 rounded-md text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          aria-label={`Excluir assinatura ${sub.planName}`}
                          title="Excluir assinatura"
                          onClick={() => handleDelete(sub)}
                          disabled={!(sub.status === 'ACTIVE' || sub.status === 'EXPIRED')}
                        >
                          <Trash2Icon className="h-5 w-5" />
                        </button>
                      </div>
                    </Permission>

                  </div>
                </div>
              </div>

              <div
                className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3"
                style={{ gridAutoRows: "1fr" }}
              >
                <div className="h-full">
                  <InfoCard
                    label="Próximo vencimento"
                    value={formatDate(sub.nextDueDate)}
                    icon={<CalendarIcon className="h-4 w-4" />}
                  />
                </div>
                <div className="h-full">
                  <InfoCard
                    label="Total a ser pago"
                    value={formatCurrency(sub.value)}
                    icon={<DollarSignIcon className="h-4 w-4" />}
                  />
                </div>
                <div className="h-full">
                  <InfoCard
                    label="Ciclo"
                    value={getSubscriptionTypeLabel(sub.subscriptionType)}
                    icon={<RotateCcwIcon className="h-4 w-4" />}
                  />
                </div>
                <div className="h-full">
                  <InfoCard
                    label="Desde"
                    value={formatDate(sub.createdAt)}
                    icon={<ClockIcon className="h-4 w-4" />}
                  />
                </div>
              </div>

              <Disclosure>
                {({ open }) => (
                  <>
                    <DisclosureButton className="w-full px-4 py-3 bg-gray-50 text-sm font-medium text-gray-600 flex justify-between items-center">
                      <span>Detalhes completos</span>
                      <ChevronDownIcon className={`${open ? 'transform rotate-180' : ''} h-5 w-5`} />
                    </DisclosureButton>
                    <DisclosurePanel className="px-4 py-3 bg-gray-50 border-t">
                      {sub.additions?.length > 0 && (
                        <div className="mb-4">
                          <h3 className="text-sm font-medium text-gray-700 mb-2">Extras incluídos</h3>
                          <div className="space-y-2">
                            {sub.additions.map((add, i) => (
                              <div key={i} className="flex justify-between text-sm">
                                <span className="text-gray-600">{add.label}</span>
                                <span className="font-medium">+{formatCurrency(add.value)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* IDs - versão compacta */}
                      <div className="space-y-2">
                        <div>
                          <h3 className="text-sm font-medium text-gray-700 mb-1">ID Assinatura</h3>
                          <CopyableText text={sub.externalReferences.asaasSubscriptionId} />
                        </div>
                        <div>
                          <h3 className="text-sm font-medium text-gray-700 mb-1">ID Cliente</h3>
                          <CopyableText text={sub.externalReferences.asaasCustomerId} />
                        </div>
                      </div>
                    </DisclosurePanel>
                  </>
                )}
              </Disclosure>
            </div>
          ))}
      </div>
    </div >
  );
}